import chroma from 'chroma-js';
import { defineConfig } from '@chakra-ui/react';
import { generateButtonRecipe, ThemeGenButtonConfig } from './components/buttonRecipe';
import { generateCardSlotRecipe, ThemeGenCardConfig } from './components/cardRecipe';
import { generateAlertSlotRecipe, ThemeGenAlertConfig } from './components/alertRecipe';
import { generateDialogRecipe, ThemeGenDialogConfig } from './components/dialogRecipe';
import { generateTableRecipe, ThemeGenTableConfig } from './components/tableRecipe';
import { linkRecipe } from './components/linkRecipe';
import { generatePopoverRecipe } from './components/generatePopoverRecipe';
import { generateInputRecipe } from './components/inputRecipe';
import { generateNumberRecipe } from './components/numberRecipe';
import { generateHeadingRecipe } from './components/headingRecipe';
import { generateSwitchRecipe } from './components/switchRecipe';
import { generateTextAreaRecipe } from './components/textAreaRecipe';

export type BaseThemeConfig = {
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  buttons?: ThemeGenButtonConfig;
  alerts?: ThemeGenAlertConfig;
  cards?: ThemeGenCardConfig;
  dialogs?: ThemeGenDialogConfig;
  table?: ThemeGenTableConfig;
};

export function buildBaseTheme(config: BaseThemeConfig) {
  const primaryColorScheme = generateColorScheme(config.primaryColor);
  const secondaryColorScheme = generateColorScheme(config.secondaryColor);
  const tertiaryColorScheme = generateColorScheme(config.tertiaryColor);

  const buttonRecipe = generateButtonRecipe(config.buttons);
  const cardSlotRecipe = generateCardSlotRecipe(config.cards);
  const alertSlotRecipe = generateAlertSlotRecipe(config.alerts);
  const dialogSlotRecipe = generateDialogRecipe(config.dialogs);
  const tableSlotRecipe = generateTableRecipe(config.table);
  const popoverSlotRecipe = generatePopoverRecipe({});
  const inputRecipe = generateInputRecipe();
  const numberRecipe = generateNumberRecipe();
  const headingRecipe = generateHeadingRecipe();
  const switchRecipe = generateSwitchRecipe();
  const textAreaRecipe = generateTextAreaRecipe();

  return defineConfig({
    globalCss: {
      body: {
        bg: 'gray.50'
      }
    },
    theme: {
      breakpoints: {
        sm: '30em',
        md: '48em',
        lg: '62em',
        xl: '80em',
        '2xl': '96em',
        full: '2048px'
      },
      recipes: {
        button: buttonRecipe,
        link: linkRecipe,
        input: inputRecipe,
        heading: headingRecipe,
        textarea: textAreaRecipe
      },
      slotRecipes: {
        card: cardSlotRecipe,
        alert: alertSlotRecipe,
        dialog: dialogSlotRecipe,
        table: tableSlotRecipe,
        popover: popoverSlotRecipe,
        numberInput: numberRecipe,
        switch: switchRecipe
      },
      tokens: {
        colors: {
          primary: primaryColorScheme,
          secondary: secondaryColorScheme,
          tertiary: tertiaryColorScheme,
          rowStripe: {
            value: config.table?.stripedColor ?? primaryColorScheme[50].value
          }
        }
      },
      semanticTokens: {
        colors: {
          bg: {
            info: { value: '#f7faff' }
          },
          primary: generateColorSchemeTokens('primary'),
          secondary: generateColorSchemeTokens('secondary'),
          tertiary: generateColorSchemeTokens('tertiary')
        }
      }
    }
  });
}

function generateColorSchemeTokens(colorToken: string) {
  return {
    solid: { value: `{colors.${colorToken}.500}` },
    contrast: { value: `{colors.${colorToken}.100}` },
    fg: { value: `{colors.${colorToken}.700}` },
    muted: { value: `{colors.${colorToken}.100}` },
    subtle: { value: `{colors.${colorToken}.200}` },
    emphasized: { value: `{colors.${colorToken}.300}` },
    focusRing: { value: `{colors.${colorToken}.500}` }
  };
}

function generateColorScheme(baseColor: string): {
  100: { value: string };
  200: { value: string };
  300: { value: string };
  400: { value: string };
  500: { value: string };
  600: { value: string };
  700: { value: string };
  800: { value: string };
  900: { value: string };
  50: { value: string };
  DEFAULT: { value: string };
} {
  return {
    DEFAULT: { value: baseColor },
    50: { value: chroma(baseColor).alpha(0.05).hex() },
    100: { value: chroma(baseColor).alpha(0.2).hex() },
    200: { value: chroma(baseColor).alpha(0.4).hex() },
    300: { value: chroma(baseColor).alpha(0.6).hex() },
    400: { value: chroma(baseColor).alpha(0.8).hex() },
    500: { value: baseColor }, // Base color
    600: { value: chroma(baseColor).saturate(0.5).hex() },
    700: { value: chroma(baseColor).saturate(1).hex() },
    800: { value: chroma(baseColor).saturate(1.5).hex() },
    900: { value: chroma(baseColor).saturate(2).hex() }
  };
}
